import { createSlice } from "@reduxjs/toolkit";
import { isIOS, isFullscreenSupported, isInFullscreen } from "../../utilities";
import StorageManager from "../../utils/StorageManager";


// Load UI settings from localStorage
const loadedUISettings = StorageManager.loadUISettings();

// Initial state for the settings slice
const initialState = {
  fullscreenEnabled: isInFullscreen(), // Sync with actual browser state
  fullscreenSupported: isFullscreenSupported(), // Track if fullscreen is supported
  autoSpinCount: 10,
  autoPlaySpeed: loadedUISettings.autoPlaySpeed || "auto", // Load from localStorage
  pendingAutoPlaySpeed: null, // Speed to apply after current spin completes
  autoPlayActive: false,
  autoplayPausedSpins: null, // Saved remaining spins when autoplay pauses for a feature game
  remainingAutoSpins: 10, // Initialize with the same value as autoSpinCount
  simulationModeEnabled: false,
  buyFreeSpinsMode: false, // Flag to trigger buy free spins simulation
  soundLevelAdjustmentEnabled: false, // Enable/disable individual sound level adjustment
  manualStopCount: 0, // Track manual stop presses
  manualStopTrackingEnabled: true, // Enable/disable manual stop tracking
  turboSuggestionPending: false, // Flag to show turbo suggestion popup on next non-winning round
  // Responsible gambling settings
  stopOnAnyWin: false,
  stopOnBonusGame: false,
  stopOnSingleWinExceeds: false,
  stopOnBalanceIncreases: false,
  stopOnBalanceDecreases: false,
  singleWinExceedsAmount: "",
  balanceIncreasesAmount: "",
  balanceDecreasesAmount: "",
};

// Create the settings slice
const settingsSlice = createSlice({
  name: "settings",
  initialState,
  reducers: {
    // Toggle fullscreen mode with iOS safety
    toggleFullscreen(state) {
      // Don't attempt fullscreen on iOS
      if (isIOS()) {
        return;
      }

      // Check if fullscreen is supported
      if (!state.fullscreenSupported) {
        return;
      }

      const element = document.documentElement;

      if (!state.fullscreenEnabled) {
        // Try different fullscreen methods for cross-browser compatibility
        const requestFullscreen =
          element.requestFullscreen ||
          element.webkitRequestFullscreen ||
          element.mozRequestFullScreen ||
          element.msRequestFullscreen;

        if (requestFullscreen) {
          requestFullscreen.call(element).catch(() => {
            // Don't update state if fullscreen failed
            return;
          });
          state.fullscreenEnabled = true;
        }
      } else {
        // Exit fullscreen
        const exitFullscreen =
          document.exitFullscreen ||
          document.webkitExitFullscreen ||
          document.mozCancelFullScreen ||
          document.msExitFullscreen;

        if (exitFullscreen) {
          exitFullscreen.call(document).catch(() => {
            // Fail silently
          });
        }
        state.fullscreenEnabled = false;
      }
    },

    // Explicitly close fullscreen mode
    closeFullscreen(state) {
      if (isIOS()) return; // No-op on iOS

      const exitFullscreen =
        document.exitFullscreen ||
        document.webkitExitFullscreen ||
        document.mozCancelFullScreen ||
        document.msExitFullscreen;

      if (exitFullscreen && document.fullscreenElement) {
        exitFullscreen.call(document).catch(() => {
          // Fail silently
        });
      }
      state.fullscreenEnabled = false; // Always update the state
    },

    // Sync fullscreen state with browser (called by event listeners)
    syncFullscreenState(state) {
      state.fullscreenEnabled = isInFullscreen();
    },

    setAutoSpinCount(state, action) {
      state.autoSpinCount = action.payload;
      state.remainingAutoSpins = action.payload; // Reset remaining spins when autoSpinCount is set
    },

    setAutoplaySpeed(state, action) {
      state.autoPlaySpeed = action.payload;
      
      // Save to localStorage - preserve other UI settings
      const currentUISettings = StorageManager.loadUISettings();
      StorageManager.saveUISettings({
        turboSuggestionDeclined: currentUISettings.turboSuggestionDeclined,
        autoPlaySpeed: action.payload,
      });
    },

    // Same as setAutoplaySpeed but skips localStorage write.
    // Used for temporary overrides (e.g. forcing 'auto' during bonus entry)
    // so the player's persisted speed preference survives the bonus.
    setAutoplaySpeedNoSave(state, action) {
      state.autoPlaySpeed = action.payload;
    },

    setPendingAutoplaySpeed(state, action) {
      state.pendingAutoPlaySpeed = action.payload;
      
      // Save to localStorage when pending speed is set
      const currentUISettings = StorageManager.loadUISettings();
      StorageManager.saveUISettings({
        turboSuggestionDeclined: currentUISettings.turboSuggestionDeclined,
        autoPlaySpeed: action.payload,
      });
    },

    applyPendingAutoplaySpeed(state) {
      if (state.pendingAutoPlaySpeed !== null) {
        state.autoPlaySpeed = state.pendingAutoPlaySpeed;
        state.pendingAutoPlaySpeed = null;
        
        // Save to localStorage when applying pending speed
        const currentUISettings = StorageManager.loadUISettings();
        StorageManager.saveUISettings({
          turboSuggestionDeclined: currentUISettings.turboSuggestionDeclined,
          autoPlaySpeed: state.autoPlaySpeed,
        });
      }
    },

    clearPendingAutoplaySpeed(state) {
      state.pendingAutoPlaySpeed = null;
    },

    startAutoPlay(state) {
      state.autoPlayActive = true;
      state.remainingAutoSpins = state.autoSpinCount; // Reset remaining spins to the initial autoSpinCount
    },

    stopAutoPlay(state) {
      state.autoPlayActive = false;
      state.remainingAutoSpins = 0; // Reset remaining spins when stopping autoplay
    },

    // Pause autoplay for a feature game — saves remaining spins for later resume.
    pauseAutoPlayForFeature(state) {
      if (state.autoPlayActive) {
        state.autoplayPausedSpins = state.remainingAutoSpins;
      }
      state.autoPlayActive = false;
      state.remainingAutoSpins = 0;
    },

    // Resume autoplay after a feature game (bonus) with saved remaining spins.
    // Unlike startAutoPlay which resets to autoSpinCount, this restores the saved count.
    resumeAutoPlay(state) {
      if (state.autoplayPausedSpins !== null) {
        state.autoPlayActive = true;
        state.remainingAutoSpins = state.autoplayPausedSpins;
        state.autoplayPausedSpins = null;
      }
    },

    // Clear paused spins (e.g. when responsible gambling permanently stops autoplay)
    clearAutoplayPausedSpins(state) {
      state.autoplayPausedSpins = null;
    },

    toggleSimulationMode(state) {
      state.simulationModeEnabled = !state.simulationModeEnabled;
    },

    setBuyFreeSpinsMode(state, action) {
      state.buyFreeSpinsMode = action.payload;
    },

    toggleSoundLevelAdjustment(state) {
      state.soundLevelAdjustmentEnabled = !state.soundLevelAdjustmentEnabled;
    },

    decrementRemainingAutoSpins(state) {
      // Don't decrement if it's infinite, only decrement finite values
      if (
        state.remainingAutoSpins !== Infinity &&
        state.remainingAutoSpins > 0
      ) {
        state.remainingAutoSpins -= 1;
      }
    },

    incrementManualStopCount(state) {
      if (state.manualStopTrackingEnabled) {
        state.manualStopCount += 1;
      }
    },

    disableManualStopTracking(state) {
      state.manualStopTrackingEnabled = false;
    },

    setTurboSuggestionPending(state, action) {
      state.turboSuggestionPending = action.payload;
    },


    // Responsible gambling reducers
    setStopOnAnyWin(state, action) {
      state.stopOnAnyWin = action.payload;
    },

    setStopOnBonusGame(state, action) {
      state.stopOnBonusGame = action.payload;
    },

    setStopOnSingleWinExceeds(state, action) {
      state.stopOnSingleWinExceeds = action.payload;
    },

    setStopOnBalanceIncreases(state, action) {
      state.stopOnBalanceIncreases = action.payload;
    },

    setStopOnBalanceDecreases(state, action) {
      state.stopOnBalanceDecreases = action.payload;
    },

    setSingleWinExceedsAmount(state, action) {
      state.singleWinExceedsAmount = action.payload;
    },

    setBalanceIncreasesAmount(state, action) {
      state.balanceIncreasesAmount = action.payload;
    },

    setBalanceDecreasesAmount(state, action) {
      state.balanceDecreasesAmount = action.payload;
    },

    // Reset UI settings to defaults (called on new player session)
    resetUISettings(state) {
      state.autoPlaySpeed = "auto";
      state.pendingAutoPlaySpeed = null;
      state.manualStopCount = 0;
      state.manualStopTrackingEnabled = true;
      state.turboSuggestionPending = false;
      // Responsible gambling resets
      state.stopOnAnyWin = false;
      state.stopOnBonusGame = false;
      state.stopOnSingleWinExceeds = false;
      state.stopOnBalanceIncreases = false;
      state.stopOnBalanceDecreases = false;
      state.singleWinExceedsAmount = "";
      state.balanceIncreasesAmount = "";
      state.balanceDecreasesAmount = "";
    },
  },
});

export const selectAutoPlaySpeed = (state) => state.settings.autoPlaySpeed;
export const selectAutoPlayActive = (state) => state.settings.autoPlayActive;
export const selectRemainingAutoSpins = (state) =>
  state.settings.remainingAutoSpins;
export const selectAutoSpinCount = (state) => state.settings.autoSpinCount;
export const selectPendingAutoPlaySpeed = (state) =>
  state.settings.pendingAutoPlaySpeed;
export const selectSimulationModeEnabled = (state) =>
  state.settings.simulationModeEnabled;
export const selectBuyFreeSpinsMode = (state) =>
  state.settings.buyFreeSpinsMode;
export const selectAutoplayPausedSpins = (state) =>
  state.settings.autoplayPausedSpins;
export const selectSoundLevelAdjustmentEnabled = (state) =>
  state.settings.soundLevelAdjustmentEnabled;
export const selectManualStopCount = (state) => state.settings.manualStopCount;
export const selectManualStopTrackingEnabled = (state) =>
  state.settings.manualStopTrackingEnabled;
export const selectTurboSuggestionPending = (state) =>
  state.settings.turboSuggestionPending;

// Responsible gambling selectors
export const selectStopOnAnyWin = (state) => state.settings.stopOnAnyWin;
export const selectStopOnBonusGame = (state) => state.settings.stopOnBonusGame;
export const selectStopOnSingleWinExceeds = (state) =>
  state.settings.stopOnSingleWinExceeds;
export const selectStopOnBalanceIncreases = (state) =>
  state.settings.stopOnBalanceIncreases;
export const selectStopOnBalanceDecreases = (state) =>
  state.settings.stopOnBalanceDecreases;
export const selectSingleWinExceedsAmount = (state) =>
  state.settings.singleWinExceedsAmount;
export const selectBalanceIncreasesAmount = (state) =>
  state.settings.balanceIncreasesAmount;
export const selectBalanceDecreasesAmount = (state) =>
  state.settings.balanceDecreasesAmount;

// Export the actions for components or middleware
export const {
  toggleFullscreen,
  closeFullscreen,
  syncFullscreenState,
  setAutoSpinCount,
  setAutoplaySpeed,
  setAutoplaySpeedNoSave,
  setPendingAutoplaySpeed,
  applyPendingAutoplaySpeed,
  clearPendingAutoplaySpeed,
  startAutoPlay,
  stopAutoPlay,
  pauseAutoPlayForFeature,
  resumeAutoPlay,
  clearAutoplayPausedSpins,
  decrementRemainingAutoSpins,
  toggleSimulationMode,
  setBuyFreeSpinsMode,
  toggleSoundLevelAdjustment,
  incrementManualStopCount,
  disableManualStopTracking,
  setTurboSuggestionPending,
  resetUISettings,
  setStopOnAnyWin,
  setStopOnBonusGame,
  setStopOnSingleWinExceeds,
  setStopOnBalanceIncreases,
  setStopOnBalanceDecreases,
  setSingleWinExceedsAmount,
  setBalanceIncreasesAmount,
  setBalanceDecreasesAmount,
} = settingsSlice.actions;

// Export the reducer for integration with the Redux store
export default settingsSlice.reducer;
